<template>
  <div class="relative w-full flex flex-col gap-0.5">
    <!-- Empty State -->
    <div v-if="!savedFilters.length && !searchQuery.length" class="w-full flex flex-col items-center" style="width: 276px;">
      <CreateSavedFilterIcon class="text-icon-normal mt-6" />
      <div class="w-full flex flex-col gap-1 px-7 pt-4 pb-6 text-center">
        <BaseText type="label" size="sm" class="text-text-muted">
          No {{ productName }} presets yet
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          Create and save your favorite filter combinations to access them quickly later.
        </BaseText>
      </div>
      <button class="submit-btn flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md"
      @click="$emit('createPreset')">
        <PlusIcon class="text-white" />
        <BaseText type="label" size="sm" class="text-white">
          Create new preset
        </BaseText>
      </button>
    </div>
    <div v-else-if="!savedFilters.length">
      <BaseText type="body" size="sm" class="text-text-subdued px-2.5 py-1">
        No presets match your search
      </BaseText>
    </div>
    <!-- Presets List -->
    <button v-for="(preset, index) in savedFilters" :key="preset.id" :ref="`option-${index}`" style="width: 276px;"
    class="flex py-1 pl-2 pr-1 rounded-md transition-colors duration-100 min-w-0" :class="getButtonNavClass(`option-${index}`)"
    @mouseenter="hoveredPreset = preset.id" @mouseleave="hoveredPreset = null"
    @click="$emit('applyPreset', preset)">
      <div class="flex-grow flex flex-col gap-1 min-w-0 text-left">
        <BaseText type="label" size="sm" class="text-text-muted">
          {{ preset.name }}
        </BaseText>
        <BaseText type="body" size="xs" class="text-text-normal">
          {{ getFiltersPreviewText(preset.filters) }}
        </BaseText>
      </div>
      <div class="flex-shrink-0 self-start w-6 h-6">
        <transition name="fade">
          <button v-if="hoveredPreset === preset.id || actionsMenuPreset?.ref === `option-${index}`" 
          class="p-0.5 rounded-md transition-colors duration-100"
          :class="actionsMenuPreset?.ref === `option-${index}` ? 'bg-neutral-50' : 'hover:bg-neutral-50'"
          @click.stop="showPresetActions(`option-${index}`, preset)">
            <KebabMenuIcon class="text-icon-muted transform rotate-90" />
          </button>
        </transition>
      </div>
    </button>
    <!-- Create Preset Button -->
    <button v-if="savedFilters.length" class="w-full self-start flex items-center gap-1.5 p-1.5 mt-1 rounded-md transition-colors duration-100 whitespace-nowrap"
    ref="createPresetBtn" :class="getButtonNavClass('createPresetBtn')"
    @click="$emit('createPreset')">
      <PlusIcon class="text-icon-normal" />
      <BaseText type="label" size="sm" class="text-text-muted">
        Create new preset
      </BaseText>
    </button>
    <!-- PRESET ACTIONS MENU -->
    <portal to="preset-actions-menu">
      <transition name="extra-actions">
        <div v-if="actionsMenuPreset" class="extra-actions-menu absolute" :style="actionsMenuPos"
        v-on-clickaway="() => { actionsMenuPreset = null }">
          <button v-for="(action, index) in presetActions" :key="`action-${index}`"
          class="group w-full flex items-center gap-2 p-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-alpha-50"
          :disabled="action.loading" :class="{'pointer-events-none': action.loading}"
          @click="action.action()">
            <BaseLoadingSpinnerCircle v-if="action.loading" class="text-neutral-alpha-600" :duration="1" />
            <component v-else :is="action.icon" class="text-neutral-alpha-600 transition-colors duration-100 group-hover:text-neutral-alpha-800" />
            <BaseText type="body" size="sm" class="text-neutral-alpha-600 transition-colors duration-100 group-hover:text-neutral-alpha-800">
              {{ action.name }}
            </BaseText>
          </button>
        </div>
      </transition>
    </portal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import FirebaseAPI from '@/api/firebase'

// Icons
import PlusIcon from '../../globals/Icons/PlusIcon.vue'
import KebabMenuIcon from '../../globals/Icons/KebabMenuIcon.vue'
import CreateSavedFilterIcon from '../../globals/Icons/FilterIcons/CreateSavedFilterIcon.vue'
import EditIcon from '../../globals/Icons/SidebarIcons/dropdowns/EditItemIcon.vue'
import DeleteIcon from '../../globals/Icons/SidebarIcons/dropdowns/DeleteItemIcon.vue'
import DuplicateIcon from '../../globals/Icons/CopyIconStatic.vue'

export default {
  name: 'SavedFiltersList',
  mixins: [clickaway],
  components: {
    PlusIcon,
    KebabMenuIcon,
    CreateSavedFilterIcon,
  },
  props: {
    savedFilters: {
      type: Array,
      required: true
    },
    containerRef: {
      type: HTMLElement,
      default: () => null
    },
    currentNavItem: {
      type: Object,
      default: () => null
    },
    searchQuery: {
      type: String,
      default: () => ''
    }
  },
  data () {
    return {
      hoveredPreset: null,
      actionsMenuPreset: null,
      actionsMenuPos: { top: 0, left: 0 },

      loadingDuplicate: false,
      loadingDelete: false
    }
  },
  computed: {
    ...mapGetters('SearchModule', ['getFilterCategories']),
    ...mapGetters('MiscModule', ['getTheme']),
    presetActions () {
      return [
        {
          name: 'Edit',
          icon: EditIcon,
          action: () => this.editPreset(this.actionsMenuPreset.preset)
        },
        {
          name: 'Duplicate',
          icon: DuplicateIcon,
          action: () => this.duplicatePreset(this.actionsMenuPreset.preset),
          loading: this.loadingDuplicate
        },
        {
          name: 'Delete',
          icon: DeleteIcon,
          action: () => this.deletePreset(this.actionsMenuPreset.preset),
          loading: this.loadingDelete
        }
      ]
    },
    productName () {
      switch (this.$route.meta.product) {
        case 'library': return 'Swipe File'
        case 'discovery': return 'Discovery'
        case 'spyder': return 'Spyder'
        case 'briefs': return 'Briefs'
        case 'lens': return 'Lens'
        default: return 'Swipe File'
      }
    }
  },
  mounted () {
    this.$nextTick(() => {
      switch (this.getTheme) {
        case 'blue': this.$el.style.setProperty('--button-color', '#1F69FF'); break;
        case 'green': this.$el.style.setProperty('--button-color', '#009F6D'); break;
        case 'purple': this.$el.style.setProperty('--button-color', '#6534DF'); break;
        case 'red': this.$el.style.setProperty('--button-color', '#FF5453'); break;
        case 'black': this.$el.style.setProperty('--button-color', '#121212'); break;
      }
    })
  },
  methods: {
    ...mapActions('SearchModule', ['fetchSavedFilters']),
    // ================= Data Methods ==================
    editPreset (preset) {
      this.$emit('editPreset', preset)
      this.actionsMenuPreset = null
    },
    async duplicatePreset (preset) {
      this.loadingDuplicate = true
      try {
        const duplicatePreset = {
          ...preset,
          name: incrementCopyName(preset.name),
        }
        delete duplicatePreset.id
        await FirebaseAPI.Filters.createSavedFilter(duplicatePreset)
        await this.fetchSavedFilters()
        this.actionsMenuPreset = null
        this.hoveredPreset = null
        this.$showAlert({
          type: 'success',
          message: 'Preset duplicated successfully'
        })
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: 'Error occurred while duplicating preset. See console for details.'
        })
      } finally {
        this.loadingDuplicate = false
      }
    },
    async deletePreset (preset) {
      this.loadingDelete = true
      try {
        await FirebaseAPI.Filters.deleteSavedFilter(preset.id)
        await this.fetchSavedFilters()
        this.actionsMenuPreset = null
        this.hoveredPreset = null
        this.$showAlert({
          type: 'success',
          message: 'Preset deleted successfully'
        })
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: 'Error occurred while deleting preset. See console for details.'
        })
      } finally {
        this.loadingDelete = false
      }
    },

    // ================= UI Methods ==================
    getFiltersPreviewText (filters) {
      const { componentFilters, ...basicFilters } = filters
      let previewText = Object.entries(basicFilters).reduce((text, [key, valueArray], i) => {
        const [logicalOperator, categoryId] = key.split('.')
        const category = this.getFilterCategories.find(category => category.id === categoryId)
        valueArray.forEach((value, j) => {
          const optionName = category.options.find(option => category.valueTransform(option[category.optionValueKey]) === value)?.name ?? value
          const formattedName = optionName.replace(/ /g, '\u00A0') // Replace spaces with non-breaking spaces
          text += `${logicalOperator === 'exclude' ? 'Exclude ' : ''}${formattedName}${j < valueArray.length - 1 ? ', ' : ''}`
        })
        return `${text}${i < Object.entries(basicFilters).length - 1 ? ', ' : ''}`
      }, '')
      if (componentFilters) {
        const componentValuesText = Object.entries(componentFilters).reduce((text, [key, value], i) => {
          const category = this.getFilterCategories.find(category => category.id === key)
          const valueText = category.getPresetPreviewText(value)
          return `${text}${valueText}${i < Object.entries(componentFilters).length - 1 ? ', ' : ''}`
        }, '')
        previewText += `, ${componentValuesText}`
      }
      return previewText.trim()
    },
    showPresetActions (ref, preset) {
      if (this.actionsMenuPreset?.ref === ref) {
        this.actionsMenuPreset = null
        return
      }
      if (!this.containerRef) return
      const elm = this.$refs[ref]?.[0] ?? this.$refs[ref]
      const rect = elm.getBoundingClientRect()
      const containerRect = this.containerRef.getBoundingClientRect()
      this.actionsMenuPos = { top: `${rect.top - containerRect.top + 12}px`, left: `${containerRect.width - 4}px` }
      this.actionsMenuPreset = { preset, ref }
    },
    getButtonNavClass (ref, neutralClass = '', activeClass = 'bg-neutral-25') {
      return this.currentNavItem?.ref === ref || this.actionsMenuPreset?.ref === ref
        ? activeClass
        : `${neutralClass} hover:${activeClass}`
    }
  }
}

const incrementCopyName = (name) => {
  const copyRegex = / - Copy(?: (\d+))?$/;
  const match = name.match(copyRegex);
  
  if (match) {
    // Remove the existing "- Copy" or "- Copy #" suffix
    const baseName = name.replace(copyRegex, '');
    
    // Get the current number (if exists) and increment
    const currentNumber = match[1] ? parseInt(match[1]) : 1;
    const nextNumber = currentNumber + 1;
    
    return `${baseName} - Copy ${nextNumber}`;
  } else {
    return `${name} - Copy`;
  }
}
</script>

<style scoped>
.extra-actions-menu {
  width: 152px;
  display: flex;
  flex-direction: column;
  padding: 4px;
  background-color: #100f1b;
  border-radius: 10px;
  transform: translate(0, -50%) scale(1);
  z-index: 50;
}

@property --submit-btn-opacity-1 {
syntax: '<number>';
initial-value: 0.12;
inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-color {
  syntax: '<color>';
  inherits: false;
}

.submit-btn {
  position: relative;
  left: 1px;
  width: calc(100% - 2px);
  --submit-color: var(--button-color);
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-color);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out, 
    background-color 100ms ease-in-out,
    width 200ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out, 
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 100ms ease-in-out;
}
.fade-enter-from, .fade-enter, .fade-leave-to {
  opacity: 0;
}
.fade-enter-to, .fade-leave-from {
  opacity: 1;
}
.extra-actions-enter-active, .extra-actions-leave-active {
  transition: transform 100ms ease-in-out, opacity 100ms ease-in-out;
}
.extra-actions-enter-from, .extra-actions-enter, .extra-actions-leave-to {
  opacity: 0;
  transform: translate(-12px, -50%) scale(0.9);
}
.extra-actions-enter-to, .extra-actions-leave-from {
  opacity: 1;
  transform: translate(0, -50%) scale(1);
}
</style>